import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { platformDetector } from '@/utils/platform.js'

/**
 * 应用全局状态 - 简化版（仅微信环境）
 */
export const useAppStore = defineStore('app', () => {
  // ==================== 基础状态 ====================
  
  // 用户信息
  const userInfo = ref(null)
  
  // 加载状态
  const isLoading = ref(false)
  
  // 错误信息
  const error = ref(null)
  
  // 当前步骤
  const currentStep = ref('chat') // 'form' | 'chat'

  // ==================== 微信相关状态 ====================
  
  // 微信SDK就绪状态
  const wechatReady = ref(false)
  
  // 用户交互状态（用于音频自动播放判断）
  const userHasInteracted = ref(false)
  const lastInteractionTime = ref(0)

  // ==================== 计算属性 ====================
  
  const isMobile = computed(() => platformDetector.isMobile())
  const hasUserInfo = computed(() => !!userInfo.value)
  const platformClass = computed(() => platformDetector.getPlatformClass())

  // ==================== 基础方法 ====================
  
  /**
   * 设置用户信息
   * @param {Object} info - 用户信息
   * @returns {void}
   */
  const setUserInfo = (info) => {
    userInfo.value = info
    // debug('📝 用户信息已设置:', info)
  }

  /**
   * 重启问卷
   * @returns {void}
   */
  const restartSurvey = () => {
    userInfo.value = null
    currentStep.value = 'chat'
    clearError()
    // info('🔄 问卷已重启')
  }

  /**
   * 设置加载状态
   * @param {boolean} loading - 加载状态
   * @returns {void}
   */
  const setLoading = (loading) => {
    isLoading.value = loading
  }

  /**
   * 设置错误信息
   * @param {string} errorMessage - 错误消息
   * @returns {void}
   */
  const setError = (errorMessage) => {
    error.value = errorMessage
    // error('❌ 错误:', errorMessage)
  }

  /**
   * 清除错误信息
   * @returns {void}
   */
  const clearError = () => {
    error.value = null
  }

  /**
   * 设置当前步骤
   * @param {'form'|'chat'} step - 步骤名称
   * @returns {void}
   */
  const setCurrentStep = (step) => {
    currentStep.value = step
    // info('📍 当前步骤:', step)
  }

  /**
   * 标记用户已交互（用于音频自动播放）
   * @returns {void}
   */
  const markUserInteraction = () => {
    userHasInteracted.value = true
    lastInteractionTime.value = Date.now()
    // debug('👆 用户已交互，时间:', lastInteractionTime.value)
  }

  /**
   * 设置微信SDK就绪状态
   * @param {boolean} ready - 是否就绪
   * @returns {void}
   */
  const setWechatReady = (ready) => {
    wechatReady.value = ready
    // info('🔧 微信SDK状态:', ready ? '已就绪' : '未就绪')
  }

  return {
    // 基础状态
    userInfo,
    isLoading,
    error,
    currentStep,
    
    // 微信相关状态
    wechatReady,
    userHasInteracted,
    lastInteractionTime,

    // 计算属性
    isMobile,
    hasUserInfo,
    platformClass,

    // 方法
    setUserInfo,
    restartSurvey,
    setLoading,
    setError,
    clearError,
    setCurrentStep,
    markUserInteraction,
    setWechatReady
  }
})
